import React, { FC, useMemo } from 'react';
import { TableCell, TableRow, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import NumberField from 'components/number-field';
import { ChargeFormEntry } from '../../types';
import { getTotalRow } from './utils';
import { Control, useWatch } from 'react-hook-form';
import { useGeneralChargesContext } from 'contexts/GeneralChargesContext';
import { useTranslation } from 'react-i18next';
import { formatMoney } from 'utils/helpers';

const useStyles = makeStyles((theme: any) => ({
  tableHeadRowSecond: {
    position: 'sticky',
    background: 'white',
    top: 56,
  },
  tableHeaderNumber: {
    minWidth: '100px',
    padding: theme.spacing(0.5),
  },
  numberField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      borderRadius: '5px',
      fontSize: '13px',
      display: 'flex',
      justifyContent: 'flex-end',

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        textAlign: 'right',
        width: '4rem',
        padding: '8px 6px',
      },
    },
  },
  tableChargeRow: {
    padding: `8px ${theme.spacing(0.5)}`,
    fontSize: '0.8rem',
    paddingLeft: '10px',
    cursor: 'default',
    color: 'black',
  },
}));

type Props = {
  control: Control<
    {
      charges: ChargeFormEntry[];
    },
    {
      charges: ChargeFormEntry[];
    }
  >;
  currentLetter?: string;
  hideTax: boolean;
  isOrderTypeNotHUD1: boolean;
};

export const TotalTableFooter: FC<Props> = ({
  control,
  currentLetter,
  hideTax,
  isOrderTypeNotHUD1,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const charges = useWatch({ control, name: 'charges' });
  const { hideEndorsementCode } = useGeneralChargesContext();

  const { amount, tax, buyerAtclosing, buyerBefore, sellerAtclosing, sellerBefore, otherTotal } =
    useMemo(() => getTotalRow(charges), [charges]);

  return (
    <TableRow className={classes.tableHeadRowSecond}>
      <TableCell />
      <TableCell />
      {hideEndorsementCode === false && (
        <TableCell />
      )}
      <TableCell className={classes.tableChargeRow}>{t('charges:totals')}</TableCell>
      <TableCell />
      <TableCell />

      <Tooltip title={formatMoney(amount)} arrow>
        <TableCell className={classes.tableHeaderNumber}>
          <NumberField
            disabled
            prefix="$"
            thousandSeparator
            color="secondary"
            className={classes.numberField}
            variant="outlined"
            value={amount}
          />
        </TableCell>
      </Tooltip>

      {currentLetter === 'G' && (
        <>
          <TableCell className={classes.tableHeaderNumber} />
          <TableCell className={classes.tableHeaderNumber} />
        </>
      )}

      {!hideTax && (
        <TableCell className={classes.tableHeaderNumber}>
          <NumberField
            disabled
            prefix="$"
            thousandSeparator
            color="secondary"
            className={classes.numberField}
            variant="outlined"
            value={tax}
          />
        </TableCell>
      )}

      <TableCell className={classes.tableHeaderNumber}>
        <NumberField
          disabled
          prefix="$"
          thousandSeparator
          color="secondary"
          className={classes.numberField}
          variant="outlined"
          value={buyerAtclosing}
        />
      </TableCell>

      {isOrderTypeNotHUD1 && (
        <TableCell className={classes.tableHeaderNumber}>
          <NumberField
            disabled
            prefix="$"
            thousandSeparator
            color="secondary"
            className={classes.numberField}
            variant="outlined"
            value={buyerBefore}
          />
        </TableCell>
      )}

      <TableCell className={classes.tableHeaderNumber}>
        <NumberField
          disabled
          prefix="$"
          thousandSeparator
          color="secondary"
          className={classes.numberField}
          variant="outlined"
          value={sellerAtclosing}
        />
      </TableCell>

      {isOrderTypeNotHUD1 && (
        <>
          <TableCell className={classes.tableHeaderNumber}>
            <NumberField
              disabled
              prefix="$"
              thousandSeparator
              color="secondary"
              className={classes.numberField}
              variant="outlined"
              value={sellerBefore}
            />
          </TableCell>

          <TableCell className={classes.tableHeaderNumber}>
            <NumberField
              disabled
              prefix="$"
              thousandSeparator
              color="secondary"
              className={classes.numberField}
              variant="outlined"
              value={otherTotal}
            />
          </TableCell>
        </>
      )}

      <TableCell />
    </TableRow>
  );
};
